import React, { useEffect, useState } from "react";
import { Check, Star, Sparkles } from "lucide-react";
import { Button, StatusBadge, LoadingBlock } from "@/components/ds";
import { api } from "@/lib/api";

const money = (n: number | null) => (n == null ? null : `$${n.toLocaleString()}`);
// Promo amounts can be fractional (e.g. $374.25) — show cents only when needed.
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;
const GROWTH_INTRO_PCT = 0.25;

// Positioning headline per tier (Module 47 §6-9).
const POSITIONING: Record<string, string> = {
  starter: "Essential AI Front Desk",
  growth: "Growth Engine for Contractors",
  pro: "Advanced Revenue & Operations Automation",
  custom: "Full ResFil Platform — Built Around Your Operation",
};

// Plan comparison matrix (Module 47 §10). Values summarize the server-side plan entitlements;
// "—" = not included, "✓" = included, text = the level included at that tier.
const COMPARISON: { category: string; starter: string; growth: string; pro: string; custom: string }[] = [
  { category: "AI Voice", starter: "24/7 answering", growth: "24/7 answering", pro: "+ Outbound AI (when configured)", custom: "Custom voice workflows" },
  { category: "SMS", starter: "Basic replies", growth: "Multi-step follow-up", pro: "AI-drafted messaging", custom: "Custom SMS workflows" },
  { category: "Lead Management", starter: "Capture & qualify", growth: "+ Lead recovery", pro: "+ Lost Lead Intelligence", custom: "Custom lead workflows" },
  { category: "Scheduling", starter: "✓", growth: "+ Self-service reschedule", pro: "+ Advanced sequences", custom: "Custom" },
  { category: "Estimate Follow-Up", starter: "—", growth: "24h / 3d / 7d", pro: "Fully configurable + AI", custom: "Custom" },
  { category: "Lead Recovery", starter: "—", growth: "No-show / cancellation", pro: "Advanced recovery", custom: "Custom" },
  { category: "Reviews", starter: "Basic request", growth: "Review automation", pro: "Advanced automation", custom: "Custom" },
  { category: "Campaigns", starter: "—", growth: "Reactivation + Seasonal", pro: "Advanced automation", custom: "Custom" },
  { category: "Analytics", starter: "Basic", growth: "Enhanced conversion", pro: "Predictive + Quality Coach", custom: "Custom intelligence" },
  { category: "Dispatch", starter: "—", growth: "—", pro: "Smart Dispatch + ETA", custom: "Advanced dispatch" },
  { category: "Integrations", starter: "Calendar (when available)", growth: "Expanded", pro: "Advanced", custom: "Enterprise / custom" },
  { category: "Multilingual", starter: "English + Spanish", growth: "Expanded (where available)", pro: "Advanced workflows", custom: "Custom workflows" },
  { category: "Multi-Location", starter: "1 location", growth: "1 location", pro: "Up to 3 + routing", custom: "Multi-location routing" },
  { category: "Custom Workflows", starter: "—", growth: "—", pro: "—", custom: "✓" },
];

const CALENDLY_URL = "https://calendly.com/claytondrewholdings/15min";

function openCalendly(e?: React.MouseEvent) {
  e?.preventDefault();
  const w = window as unknown as { Calendly?: { initPopupWidget: (o: { url: string }) => void } };
  if (w.Calendly) w.Calendly.initPopupWidget({ url: CALENDLY_URL });
  else window.open(CALENDLY_URL, "_blank", "noopener");
}

export default function Pricing() {
  const [plans, setPlans] = useState<any[] | null>(null);
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");
  useEffect(() => { api.publicPlans().then(setPlans).catch(() => setPlans([])); }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      <div className="text-center">
        <StatusBadge tone="blue">Simple, transparent pricing</StatusBadge>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Plans that pay for themselves</h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-600">One booked job usually covers the month. Subscription and a one-time setup fee are shown separately — we never bundle them or promise unlimited AI usage.</p>

        <div className="mt-6 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="pricing-interval-toggle">
          {([["monthly", "Monthly"], ["annual", "Annual — save 10%"]] as const).map(([value, label]) => (
            <button key={value} onClick={() => setInterval(value)} data-testid={`pricing-interval-${value}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                interval === value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {!plans ? (
        <div className="mt-12"><LoadingBlock rows={6} /></div>
      ) : (
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4" data-testid="pricing-grid">
          {plans.map((p) => (
            <PlanCard key={p.plan_type} p={p} interval={interval} />
          ))}
        </div>
      )}
      <p className="mt-8 text-center text-xs text-slate-400">Overage (when included usage is exceeded) is billed later at each plan's configured rate — never automatically in the current release.</p>

      {plans && plans.length > 0 && <ComparisonTable />}
    </div>
  );
}

function ComparisonTable() {
  const cols = [
    { key: "starter", label: "Starter" },
    { key: "growth", label: "Growth" },
    { key: "pro", label: "Pro" },
    { key: "custom", label: "Custom" },
  ] as const;
  const cell = (v: string) =>
    v === "✓" ? <Check className="mx-auto h-4 w-4 text-emerald-600" />
      : v === "—" ? <span className="text-slate-300">—</span>
        : <span className="text-slate-600">{v}</span>;
  return (
    <div className="mt-20" data-testid="pricing-comparison">
      <div className="text-center">
        <StatusBadge tone="blue">Plan comparison</StatusBadge>
        <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Compare every plan</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600">What's included at each tier. Provider-dependent features (telephony, outbound, live voice) activate once configured.</p>
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700">Capability</th>
              {cols.map((c) => (
                <th key={c.key} className={`px-4 py-3 text-center font-semibold ${c.key === "growth" ? "text-blue-700" : "text-slate-700"}`}>
                  {c.label}{c.key === "growth" && <span className="ml-1 align-middle text-[10px] font-bold uppercase text-blue-500">Popular</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map((row, i) => (
              <tr key={row.category} className={i % 2 ? "bg-slate-50/40" : "bg-white"} data-testid={`compare-row-${row.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <td className="sticky left-0 z-10 bg-inherit px-4 py-3 text-left font-medium text-slate-800">{row.category}</td>
                {cols.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-center text-xs">{cell(row[c.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnnualPrice({ p }: { p: any }) {
  const list = Number(p.annual_price || 0);
  const promo = p.annual_promotion;
  const pct = promo?.percent ? Number(promo.percent) : 0;
  const subscription = pct ? Math.round(list * (1 - pct / 100) * 100) / 100 : list;
  const setup = Number(p.annual_setup_fee || 0);
  const perMonth = subscription / 12;
  if (!list) {
    return (
      <>
        <p className="font-display text-3xl font-semibold text-slate-900">Custom Pricing</p>
        <p className="text-sm text-slate-500">Annual terms quoted per business</p>
      </>
    );
  }
  return (
    <div data-testid={`pricing-${p.plan_type}-annual`}>
      <p className="flex items-baseline gap-2">
        <span className="font-display text-4xl font-semibold text-slate-900">{money(subscription)}</span>
        {pct > 0 && <span className="text-sm text-slate-400 line-through">{money(list)}</span>}
        <span className="text-slate-500">/ year</span>
      </p>
      <p className="mt-1 text-sm text-slate-500">+ {money(setup)} one-time setup</p>
      <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-2.5">
        <p className="text-sm font-semibold text-emerald-800">{money(subscription + setup)} due today</p>
        <p className="mt-0.5 text-xs text-slate-600">
          Twelve months of service, which works out at {money(perMonth)} a month.
          {pct > 0
            ? ` Includes ${pct}% off the first year's subscription; the setup fee is not discounted, and it renews at ${money(list)} a year.`
            : ` Renews at ${money(list)} a year.`}
          {" "}Refundable within 30 days of purchase.
        </p>
      </div>
    </div>
  );
}

function PlanCard({ p, interval }: { p: any; interval: "monthly" | "annual" }) {
  const popular = !!p.popular;
  const custom = !!p.custom_pricing || !!p.contact_sales;
  const annual = interval === "annual";
  // The monthly Growth promotion is a first-month discount; annual has its own, which comes off
  // the yearly subscription. They are different offers and must never be shown together.
  const growthPromo = p.plan_type === "growth" && !custom && !annual;
  const discMonthly = growthPromo ? p.monthly_price * (1 - GROWTH_INTRO_PCT) : 0;
  const stdInitial = growthPromo ? p.monthly_price + p.setup_fee : 0;
  // The promotion discounts the FIRST MONTH ONLY — the one-time setup fee is charged in full.
  const promoInitial = growthPromo ? discMonthly + p.setup_fee : 0;
  return (
    <div
      data-testid={`pricing-${p.plan_type}`}
      className={`relative flex flex-col rounded-2xl border p-6 shadow-sm transition-shadow hover:shadow-md ${
        popular ? "border-blue-500 ring-2 ring-blue-500" : "border-slate-200"}`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-testid="pricing-growth-badge">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white shadow">
            <Star className="h-3 w-3" /> MOST POPULAR
          </span>
        </div>
      )}
      {annual && p.annual_promotion?.percent && !custom && (
        <div className="mb-2 -mt-1" data-testid="pricing-annual-promo-badge">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
            <Sparkles className="h-3 w-3" /> {p.annual_promotion.percent}% OFF FIRST YEAR
          </span>
        </div>
      )}
      {growthPromo && (
        <div className="mb-2 -mt-1" data-testid="pricing-growth-promo-badge">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
            <Sparkles className="h-3 w-3" /> 25% OFF FIRST MONTH
          </span>
        </div>
      )}
      <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600" data-testid={`pricing-${p.plan_type}-positioning`}>{POSITIONING[p.plan_type] || ""}</p>
      <h2 className="mt-0.5 font-display text-xl font-semibold text-slate-900">{p.name}</h2>
      <p className="mt-1 min-h-[2.5rem] text-xs text-slate-500">{p.tagline}</p>

      {/* Price + setup fee (always separate) */}
      <div className="mt-4" data-testid={`pricing-${p.plan_type}-price`}>
        {custom ? (
          <>
            <p className="font-display text-3xl font-semibold text-slate-900">Custom Pricing</p>
            <p className="text-sm text-slate-500">Custom Implementation</p>
          </>
        ) : annual ? (
          <AnnualPrice p={p} />
        ) : growthPromo ? (
          <>
            <p className="flex items-baseline gap-2" data-testid="pricing-growth-first-month">
              <span className="font-display text-4xl font-semibold text-slate-900">{fmt(discMonthly)}</span>
              <span className="text-sm text-slate-400 line-through">{money(p.monthly_price)}</span>
              <span className="text-slate-500">first month</span>
            </p>
            <p className="mt-1 text-sm text-slate-500" data-testid="pricing-growth-setup">
              + <span className="font-medium text-slate-700">{money(p.setup_fee)}</span> one-time setup
            </p>
            <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-2.5" data-testid="pricing-growth-initial">
              <p className="text-sm font-semibold text-emerald-800">
                {fmt(promoInitial)} due today{" "}
                <span className="font-normal text-emerald-600/70 line-through">{money(stdInitial)}</span>
              </p>
              <p className="mt-0.5 text-xs text-slate-600">
                Discount applies to the first month only. The one-time setup fee is not discounted. Renews at {money(p.monthly_price)}/month starting with the second month.
              </p>
            </div>
          </>
        ) : (
          <>
            <p>
              <span className="font-display text-4xl font-semibold text-slate-900">{money(p.monthly_price)}</span>
              <span className="text-slate-500"> / month</span>
            </p>
            <p className="mt-1 text-sm text-slate-500" data-testid={`pricing-${p.plan_type}-setup`}>+ {money(p.setup_fee)} one-time setup</p>
          </>
        )}
      </div>

      {/* Included allowances (exact numbers, never vague) */}
      <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm" data-testid={`pricing-${p.plan_type}-allowance`}>
        {custom ? (
          <>
            <p className="text-slate-700">Custom AI voice usage</p>
            <p className="text-slate-700">Custom SMS usage</p>
            <p className="text-slate-500">Custom locations, phone numbers, workflows & integrations</p>
          </>
        ) : (
          <>
            <p className="text-slate-700"><b>{p.voice_minutes.toLocaleString()}</b> AI voice minutes</p>
            <p className="text-slate-700"><b>{p.sms_messages.toLocaleString()}</b> SMS messages</p>
          </>
        )}
      </div>

      <div className="mt-5">
        {custom ? (
          <Button className="w-full" variant="outline" onClick={openCalendly} data-testid={`pricing-${p.plan_type}-cta`}>Book a Consultation</Button>
        ) : (
          <Button className="w-full" variant={popular ? "primary" : "outline"} onClick={openCalendly} data-testid={`pricing-${p.plan_type}-cta`}>Get Started</Button>
        )}
      </div>

      {!custom && (
        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3" data-testid={`pricing-${p.plan_type}-setup-note`}>
          <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-800"><Sparkles className="h-3.5 w-3.5" /> Professional Setup &amp; Onboarding</p>
          <p className="mt-1 text-xs text-slate-600">Your one-time setup fee covers our team configuring your account, AI receptionist, business knowledge, scheduling rules, supported integrations, testing and launch preparation.</p>
        </div>
      )}

      <ul className="mt-6 space-y-2.5">
        {p.features.map((f: string) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}
